// src/components/FertilizacionModal/FertilizacionModal.jsx
import React, { useEffect, useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Box,
    Typography,
    Alert,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    Straighten as StraightenIcon
} from '@mui/icons-material';

// ⬇️ Nuevo: input dinámico integrado en el modal
import DynamicPropertiesInput from '../DynamicPropertiesInput/DynamicPropertiesInput';

const FertilizacionModal = ({
                                open,
                                onClose,
                                onConfirm,
                                isProcessing = false,
                                // Prefill opcional
                                initialAnchoFaja = '',
                                initialRangos = { rangoMin: '', rangoMedio: '', rangoMax: '' },
                                // Propiedades dinámicas integradas
                                templates = [],
                                initialCustomProps = [],
                                // Modo: 'TXT' (normal) | 'ZIP_POINTS' (solo ancho)
                                mode = 'TXT'
                            }) => {
    /* ─────── state ─────────────────────────────────────────── */
    const [form, setForm] = useState({
        anchoFaja: '',
        rangoMin: '',
        rangoMedio: '',
        rangoMax: ''
    });
    const [error, setError] = useState(null);

    // ⬇️ Estado para las propiedades dinámicas
    const [customProps, setCustomProps] = useState(Array.isArray(initialCustomProps) ? initialCustomProps : []);

    // Cuando mode = 'ZIP_POINTS' solo mostramos "Ancho de faja"
    const onlyAnchoFaja    = mode === 'ZIP_POINTS';
    const showRanges       = !onlyAnchoFaja;
    const showDynamicProps = !onlyAnchoFaja;

    /* ─────── cargar valores iniciales al abrir ─────────────── */
    useEffect(() => {
        if (open) {
            setForm({
                anchoFaja:  String(initialAnchoFaja ?? ''),
                rangoMin:   String(initialRangos?.rangoMin ?? ''),
                rangoMedio: String(initialRangos?.rangoMedio ?? ''),
                rangoMax:   String(initialRangos?.rangoMax ?? '')
            });
            setCustomProps(Array.isArray(initialCustomProps) ? initialCustomProps : []);
            setError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialAnchoFaja, initialRangos?.rangoMin, initialRangos?.rangoMedio, initialRangos?.rangoMax]);

    /* ─────── helpers ───────────────────────────────────────── */
    const reset = () => {
        setForm({
            anchoFaja: '',
            rangoMin: '',
            rangoMedio: '',
            rangoMax: ''
        });
        setCustomProps([]);
        setError(null);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    /* Validación condicional:
       – ancho > 0
       – si corresponde, los tres rangos son números y min < medio < max
    */
    const validate = () => {
        const aF = parseFloat(form.anchoFaja);

        if (Number.isNaN(aF) || aF <= 0) {
            setError('Ingrese un ancho de faja mayor que cero');
            return false;
        }

        if (showRanges) {
            const rMin = parseFloat(form.rangoMin);
            const rMid = parseFloat(form.rangoMedio);
            const rMax = parseFloat(form.rangoMax);

            if ([rMin, rMid, rMax].some((v) => Number.isNaN(v))) {
                setError('Complete los tres rangos con números válidos');
                return false;
            }
            if (!(rMin < rMid && rMid < rMax)) {
                setError('Asegúrese de que:  mínimo < medio < máximo');
                return false;
            }
        }

        setError(null);
        return true;
    };

    const cleanCustomProps = (rows) =>
        (rows || [])
            .filter(r => r && String(r.label || '').trim() !== '')
            .map(r => ({
                label: String(r.label || '').trim(),
                value: String(r.value ?? '').trim()
            }));

    /* ─────── submit ────────────────────────────────────────── */
    const handleSubmit = () => {
        if (!validate()) return;

        onConfirm({
            anchoFaja: parseFloat(form.anchoFaja),
            /* rangos deben viajar en el orden [min, medio, max] */
            rangos: showRanges
                ? [
                    parseFloat(form.rangoMin),
                    parseFloat(form.rangoMedio),
                    parseFloat(form.rangoMax)
                ]
                : [null, null, null],
            // ⬇️ incluir también las propiedades dinámicas (solo en modo TXT)
            customProps: showDynamicProps ? cleanCustomProps(customProps) : []
        });
        reset();
    };

    /* ─────── UI ────────────────────────────────────────────── */
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StraightenIcon color="primary" />
                    <Typography variant="h6">Datos de Fertilización</Typography>
                </Box>

                <IconButton size="small" onClick={handleClose} disabled={isProcessing}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {onlyAnchoFaja
                        ? 'Ingrese el ancho de faja (en metros).'
                        : 'Ingrese el ancho de faja (en metros) y los rangos de diferencia de dosis (%).'}
                </Alert>

                {/* ancho de faja */}
                <TextField
                    label="Ancho de faja (m)"
                    type="number"
                    fullWidth
                    value={form.anchoFaja}
                    onChange={(e) => setForm((f) => ({ ...f, anchoFaja: e.target.value }))}
                    inputProps={{ min: 0, step: 'any' }}
                    error={!!error}
                />

                {/* rangos */}
                {showRanges && (
                    <>
                        <TextField
                            label="Rango mínimo (%)"
                            type="number"
                            fullWidth
                            sx={{ mt: 2 }}
                            value={form.rangoMin}
                            onChange={(e) => setForm((f) => ({ ...f, rangoMin: e.target.value }))}
                            inputProps={{ min: 0, max: 100, step: 'any' }}
                            disabled={isProcessing}
                        />
                        <TextField
                            label="Rango medio (%)"
                            type="number"
                            fullWidth
                            sx={{ mt: 2 }}
                            value={form.rangoMedio}
                            onChange={(e) => setForm((f) => ({ ...f, rangoMedio: e.target.value }))}
                            inputProps={{ min: 0, max: 100, step: 'any' }}
                            disabled={isProcessing}
                        />
                        <TextField
                            label="Rango máximo (%)"
                            type="number"
                            fullWidth
                            sx={{ mt: 2 }}
                            value={form.rangoMax}
                            onChange={(e) => setForm((f) => ({ ...f, rangoMax: e.target.value }))}
                            inputProps={{ min: 0, max: 100, step: 'any' }}
                            disabled={isProcessing}
                        />
                    </>
                )}

                {/* mensaje de error general */}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* ─────────────────────────────
                    Propiedades dinámicas (al final)
                    ───────────────────────────── */}
                {showDynamicProps && (
                    <>
                        <Divider sx={{ my: 3 }} />
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Propiedades de la plantilla (opcional)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Añade pares <b>Propiedad</b> y <b>Valor</b> que quieras reutilizar en plantillas o reportes.
                        </Typography>

                        <DynamicPropertiesInput
                            value={customProps}
                            onChange={setCustomProps}
                            templates={templates}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button variant="outlined" onClick={handleClose} disabled={isProcessing}>
                    Cancelar
                </Button>
                <Button variant="contained" onClick={handleSubmit} disabled={isProcessing}>
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FertilizacionModal;
